import React, { useEffect, useId, useRef, useState } from "react";

import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { ComboboxBase } from "@/src/components/form-fields/Combobox";
import matchesSearch from "@/src/utils/search";

import type { JSX } from "react";

import type { ComboboxOption } from "@/src/components/form-fields/MultiCombobox";

import type { AppState } from "../../reducers";

interface BulkStorefrontProjectFieldProps {
  isDisabled: boolean;
  isSaving: boolean;
  value: string | null;
  onChange: (projectUuid: string) => void;
}

function getTrigger(field: HTMLElement | null): HTMLElement | null {
  return field?.querySelector<HTMLElement>('[role="combobox"]') ?? null;
}

function refocusTriggerIfIdle(field: HTMLElement | null): void {
  if (document.activeElement !== document.body) {
    return;
  }

  getTrigger(field)?.focus({ preventScroll: true });
}

function BulkStorefrontProjectField({
  isDisabled,
  isSaving,
  onChange,
  value,
}: BulkStorefrontProjectFieldProps): JSX.Element {
  const { t } = useTranslation("editor-bulk");
  const projects = useSelector(
    (state: AppState) => state.fetchReducer.projects,
  );
  const hintId = useId();
  const fieldRef = useRef<HTMLDivElement>(null);
  const hasPickedRef = useRef<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [comboboxKey, setComboboxKey] = useState<number>(0);
  const hasSearch = search.length > 0;

  useEffect(() => {
    if (isSaving || !hasPickedRef.current) {
      return;
    }

    hasPickedRef.current = false;
    refocusTriggerIfIdle(fieldRef.current);
  }, [isSaving]);

  useEffect(() => {
    const trigger = getTrigger(fieldRef.current);

    if (!hasSearch || trigger === null) {
      return undefined;
    }

    const observer = new MutationObserver(() => {
      if (trigger.getAttribute("aria-expanded") === "true") {
        return;
      }

      setSearch("");
      setComboboxKey((key) => key + 1);
    });

    observer.observe(trigger, { attributeFilter: ["aria-expanded"] });

    return (): void => {
      observer.disconnect();
    };
  }, [hasSearch]);

  useEffect(() => {
    if (comboboxKey > 0) {
      refocusTriggerIfIdle(fieldRef.current);
    }
  }, [comboboxKey]);

  const projectOptionList: ComboboxOption[] = (
    projects ? projects.data : []
  ).map((project) => ({
    label: project.name,
    value: project.uuid,
  }));

  const currentValue = value !== null && value.length > 0 ? value : null;
  const currentOption = projectOptionList.find(
    (option) => option.value === currentValue,
  );
  const inaccessibleOption =
    currentValue !== null && currentOption === undefined && projects?.isFetched
      ? { label: t("storefrontProjectInaccessible"), value: currentValue }
      : null;
  const currentFirstOptionList = currentOption
    ? [
        currentOption,
        ...projectOptionList.filter(
          (option) => option.value !== currentOption.value,
        ),
      ]
    : projectOptionList;
  const unsearchedOptionList = inaccessibleOption
    ? [inaccessibleOption, ...currentFirstOptionList]
    : currentFirstOptionList;
  const trimmedSearch = search.trim();
  const visibleOptionList =
    trimmedSearch.length > 0
      ? currentFirstOptionList.filter((option) =>
          matchesSearch(option.label, trimmedSearch),
        )
      : unsearchedOptionList;
  const leadingOption = currentOption ?? inaccessibleOption;
  const selectedLabel =
    currentValue === null
      ? null
      : (leadingOption?.label ?? t("storefrontProjectLoading"));

  const handleValueChange = (projectUuid: string): void => {
    if (isDisabled || projectUuid === currentValue) {
      return;
    }

    hasPickedRef.current = true;
    onChange(projectUuid);
  };

  return (
    <div className="flex w-full max-w-xl flex-col gap-1.5">
      <p className="text-sm" id={hintId}>
        {t("storefrontProjectRequiredHint")}
      </p>

      <div
        ref={fieldRef}
        aria-labelledby={hintId}
        role="group"
        title={inaccessibleOption?.value}
      >
        <ComboboxBase
          key={comboboxKey}
          disabled={isDisabled}
          emptyLabel={t("storefrontProjectEmpty")}
          onSearchChange={setSearch}
          onValueChange={handleValueChange}
          options={visibleOptionList}
          placeholder={t("selectStorefrontProject")}
          searchPlaceholder={t("storefrontProjectSearch")}
          selectedLabel={selectedLabel}
          value={currentValue}
        />
      </div>

      <p className="text-muted-foreground text-xs">
        {t("storefrontProjectHelp")}
      </p>

      {isSaving ? (
        <p
          className="text-muted-foreground flex items-center gap-1.5 text-xs"
          role="status"
        >
          <Loader2 className="size-3 animate-spin" />
          {t("storefrontProjectSaving")}
        </p>
      ) : null}
    </div>
  );
}

export default BulkStorefrontProjectField;
